// src/pages/ProductsPage.jsx - Updated with all 4 products
import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import productsHeroBg from '../assets/images/services/apps-cartoon.jpg';
import schematzPreview from '../assets/images/products/Schematz.png';
import workEikonPreview from '../assets/images/products/WorkEikon.png';
import quinthexPreview from '../assets/images/products/Quinthex.jpg';
import fundtzPreview from '../assets/images/products/Fundtz.jpg';

const ProductsPage = () => {
  const { isDark } = useTheme();

  const products = [
    {
      id: 'schematz',
      title: 'Schematz',
      tagline: 'Comprehensive ERP Solution',
      icon: '🏢',
      description: 'Cutting-edge ERP solution designed to help businesses streamline operations, improve decision-making, and achieve sustainable growth with real-time insights and automation.',
      features: ['Financial Management', 'Supply Chain', 'Manufacturing', 'Business Intelligence'],
      link: '/products/schematz',
      badge: 'ERP SOLUTION',
      image: schematzPreview,
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      id: 'workeikon',
      title: 'WorkEikon',
      tagline: 'Enterprise EHS Management Software',
      icon: '🛡️',
      description: 'Comprehensive environmental, health, and safety management solution that streamlines compliance, incident tracking, and risk assessment for modern workplaces.',
      features: ['Incident Management', 'Hazard Reporting', 'Audit Management', 'Compliance Tracking'],
      link: '/products/workeikon',
      badge: 'EHS SOFTWARE',
      image: workEikonPreview,
      gradient: 'from-blue-700 to-blue-600'
    },
    {
      id: 'quinthex',
      title: 'Quinthex',
      tagline: 'Cloud-Based Learning Management System',
      icon: '🎓',
      description: 'Powerful learning management platform that provides all the tools needed to educate and train employees at all levels, with customizable solutions for various industries.',
      features: ['Training Records', 'Certificate Management', 'Competency Assessment', 'Training Matrix'],
      link: '/products/quinthex',
      badge: 'LMS SOFTWARE',
      image: quinthexPreview,
      gradient: 'from-blue-500 to-purple-600'
    },
    {
      id: 'fundtz',
      title: 'Fundtz',
      tagline: 'Invoicing, Work Order & Quotation Software',
      icon: '💰',
      description: 'All-in-one solution for managing invoicing, work orders, and quotations. Simplify financial operations and automate workflows for small to large enterprises.',
      features: ['Work Orders', 'Quotations', 'Invoicing', 'Client Management'],
      link: '/products/fundtz',
      badge: 'FINANCIAL SOFTWARE',
      image: fundtzPreview,
      gradient: 'from-green-500 to-emerald-600'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className={`relative py-16 sm:py-20 lg:py-24 overflow-hidden ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="absolute inset-0 z-0">
          <img 
            src={productsHeroBg}
            alt="DOROTS products"
            className="w-full h-full object-cover object-center"
          />
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-r from-black/85 via-black/70 to-black/50' 
              : 'bg-gradient-to-r from-black/85 via-black/70 to-black/50'
          }`} />
        </div>

        {isDark && (
          <>
            <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-700/15 rounded-full blur-3xl z-0" />
            <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-3xl z-0" />
          </>
        )}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4">
              <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block  ${
                isDark 
                  ? 'text-blue-400 bg-blue-700/20 border border-blue-600/30' 
                  : 'text-blue-700 bg-white border border-blue-300'
              }`}>
                OUR PRODUCTS
              </span>
            </div>
            
            <h1 className={`text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-white'}`}>
              Software Solutions{' '}
              <span className="bg-gradient-to-r from-blue-700 via-blue-500 to-blue-600 bg-clip-text text-transparent">
                Built for Impact
              </span>
            </h1>
            
            <p className={`text-lg sm:text-xl leading-relaxed ${isDark ? 'text-gray-200' : 'text-white'}`}>
              Discover our proven software products that have helped businesses transform their operations,
              improve safety, enhance workforce capabilities, and streamline financial management.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid - 4 products now */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {products.map((product, index) => (
              <Link to={product.link} key={index}>
                <div className={`group overflow-hidden rounded-2xl transition-all duration-300 hover:transform hover:scale-[1.02] ${
                  isDark 
                    ? 'bg-white/5 border border-white/10 hover:border-blue-700/30' 
                    : 'bg-gray-50 border border-gray-200 hover:border-blue-700 hover:shadow-xl'
                }`}>
                  {/* Product Preview Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className={`absolute top-4 right-4 text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r ${product.gradient} text-white`}>
                      {product.badge}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="text-3xl">{product.icon}</div>
                      <div>
                        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {product.title}
                        </h3>
                        <p className={`text-sm ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>
                          {product.tagline}
                        </p>
                      </div>
                    </div>
                    
                    <p className={`mb-4 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {product.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mt-4">
                      {product.features.map((feature, idx) => (
                        <span key={idx} className={`text-xs px-2 py-1 rounded-full ${
                          isDark 
                            ? 'bg-white/5 text-gray-400' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    <div className="mt-6 flex items-center gap-2 text-blue-700 group-hover:gap-3 transition-all">
                      <span className="text-sm font-medium">Learn More</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Products */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-gray-900/30' : 'bg-blue-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-3xl p-8 md:p-12 text-center ${
            isDark 
              ? 'bg-gradient-to-r from-blue-700/10 to-blue-500/10 border border-white/10' 
              : 'bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-100'
          }`}>
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Why Choose Our{' '}
              <span className="bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-transparent">
                Products
              </span>
            </h2>
            <p className={`text-lg mb-12 max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Built with enterprise-grade security and designed for real-world use
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-3xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>🔒</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Enterprise Security
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Bank-grade encryption and secure data storage
                </p>
              </div>
              
              <div className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-3xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>⚡</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Proven Results
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Trusted by businesses across industries
                </p>
              </div>
              
              <div className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-3xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>🔄</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Continuous Updates
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Regular feature updates and improvements
                </p>
              </div>

              <div className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-3xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>📱</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Mobile Ready
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Access anywhere, on any device
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`py-16 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-700">500+</div>
              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Businesses Trust Us</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-700">50K+</div>
              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Invoices Processed</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-700">99.9%</div>
              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Uptime Guarantee</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-700">24/7</div>
              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Customer Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-gray-900/30' : 'bg-blue-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Ready to Simplify Your Financial Operations?
          </h2>
          <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Join thousands of businesses using Fundtz to manage invoicing, work orders, and quotations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-700 to-blue-600 text-white font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                Start Free Trial
              </button>
            </Link>
            <Link to="/products/fundtz">
              <button className={`px-8 py-3 rounded-xl border font-semibold transition-all duration-300 ${
                isDark 
                  ? 'border-white/20 text-white hover:bg-white/10' 
                  : 'border-blue-300 text-blue-700 hover:bg-blue-100'
              }`}>
                View Demo
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;